import { ReactNode, useCallback, useRef, useState } from "react";
import styled from "@emotion/styled";
import { text, nav, fontSize, spacing, borderRadius } from "@atlas-int/tokens";

// ─── Inline styled components (extracted from navigationBar/Components) ─────
// These are minimal versions of the nav bar primitives needed by this trigger.
// If the full nav bar components are extracted to a separate package, these can
// be replaced with imports from that package.

const OptionIcon = styled.div`
  width: ${spacing[16]};
  height: ${spacing[16]};
  text-align: center;
  padding: ${spacing[2]};
  display: flex;
  justify-content: center;
  align-items: center;

  & img {
    margin: auto;
    width: ${spacing[12]};
  }
`;

const OptionLabel = styled.span`
  display: block;
  color: ${text.onDark};
  text-align: center;
  font-size: ${fontSize.xs};
`;

const OptionsListItem = styled.button`
  /* Reset the browser's default button chrome so it matches the old <div>. */
  border: none;
  background: none;
  font: inherit;
  padding: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;

  &:hover > div,
  &:focus-visible > div {
    background-color: ${nav.surface};
    border-radius: ${borderRadius.lg};
  }
`;

// ─── Component ──────────────────────────────────────────────────────────────

interface NavModalTriggerProps {
  icon: string;
  iconAlt: string;
  label: string;
  isNavOpen: boolean;
  menuOptionClassName?: string;
  onOpen?: () => void;
  defaultOpen?: boolean;
  children: (props: {
    isOpen: boolean;
    onClose: () => void;
    onToggle: () => void;
  }) => ReactNode;
}

const NavModalTrigger = ({
  icon,
  iconAlt,
  label,
  isNavOpen,
  menuOptionClassName,
  onOpen,
  defaultOpen = false,
  children,
}: NavModalTriggerProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // On close, return focus to the trigger button.
  const restoreFocus = useCallback(() => triggerRef.current?.focus(), []);

  const onToggle = useCallback(() => {
    if (!isOpen) {
      onOpen?.();
    } else {
      restoreFocus();
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, onOpen, restoreFocus]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    restoreFocus();
  }, [restoreFocus]);

  return (
    <>
      <OptionsListItem
        ref={triggerRef}
        type="button"
        className={menuOptionClassName}
        onClick={onToggle}
      >
        <OptionIcon>
          <img src={icon} alt={iconAlt} />
        </OptionIcon>
        {isNavOpen === true && <OptionLabel>{label}</OptionLabel>}
      </OptionsListItem>
      {children({ isOpen, onClose, onToggle })}
    </>
  );
};

export default NavModalTrigger;
